import { renderSanitizedMermaidSvg } from "./mermaid-safe-render"

export type MermaidSvgNodePosition = { x: number; y: number }

export type MermaidSvgLayoutOptions = {
  theme: "dark" | "light"
  padding?: number
}

type MatrixLike = Pick<DOMMatrixReadOnly, "a" | "b" | "c" | "d" | "e" | "f">

function transformPoint(matrix: MatrixLike, x: number, y: number): { x: number; y: number } {
  return { x: matrix.a * x + matrix.c * y + matrix.e, y: matrix.b * x + matrix.d * y + matrix.f }
}

function getNodeIdFromMermaidNodeElement(nodeElement: Element): string | null {
  const dataId =
    nodeElement.getAttribute("data-id") ||
    nodeElement.getAttribute("data-node") ||
    nodeElement.getAttribute("data-entity")
  if (dataId?.trim()) return dataId.trim()

  const title = nodeElement.querySelector("title")?.textContent.trim()
  if (title) return title

  const rawId = nodeElement.getAttribute("id")?.trim()
  if (!rawId) return null

  // Common Mermaid patterns: "flowchart-A-0", "graph-A-0", "flowchart-A"
  const match = rawId.match(/^[^-]+-(.+?)(?:-\d+)?$/)
  return match?.[1] || rawId
}

function getTopLeftInSvgCoordinates(nodeElement: SVGGElement): { x: number; y: number } | null {
  const bbox = nodeElement.getBBox()
  const ctm = nodeElement.getCTM()

  if (!ctm) return { x: bbox.x, y: bbox.y }

  const points = [
    transformPoint(ctm, bbox.x, bbox.y),
    transformPoint(ctm, bbox.x + bbox.width, bbox.y),
    transformPoint(ctm, bbox.x, bbox.y + bbox.height),
    transformPoint(ctm, bbox.x + bbox.width, bbox.y + bbox.height),
  ]

  const minX = Math.min(...points.map((point) => point.x))
  const minY = Math.min(...points.map((point) => point.y))

  return { x: minX, y: minY }
}

function normalizePositions(
  positions: Record<string, MermaidSvgNodePosition>,
  padding: number
): Record<string, MermaidSvgNodePosition> {
  const allPositions = Object.values(positions)
  if (allPositions.length === 0) return {}

  const minX = Math.min(...allPositions.map((pos) => pos.x))
  const minY = Math.min(...allPositions.map((pos) => pos.y))

  const normalized: Record<string, MermaidSvgNodePosition> = {}
  for (const [nodeId, pos] of Object.entries(positions)) {
    normalized[nodeId] = { x: pos.x - minX + padding, y: pos.y - minY + padding }
  }

  return normalized
}

export async function getMermaidSvgNodePositions(
  mermaidCode: string,
  { theme, padding = 48 }: MermaidSvgLayoutOptions
): Promise<Record<string, MermaidSvgNodePosition>> {
  if (typeof document === "undefined") return {}

  const trimmedCode = mermaidCode.trim()
  if (!trimmedCode) return {}

  const svg = await renderSanitizedMermaidSvg(`mermaid-layout-${Date.now()}`, trimmedCode, {
    theme: theme === "dark" ? "dark" : "default",
    fontFamily: "inherit",
  })

  const container = document.createElement("div")
  container.style.position = "absolute"
  container.style.left = "-10000px"
  container.style.top = "-10000px"
  container.style.visibility = "hidden"
  container.style.pointerEvents = "none"
  container.style.width = "0"
  container.style.height = "0"
  container.style.overflow = "hidden"
  container.innerHTML = svg

  document.body.appendChild(container)
  try {
    const svgElement = container.querySelector("svg")
    if (!svgElement) return {}

    const nodeElements = Array.from(svgElement.querySelectorAll("g.node")) as SVGGElement[]
    const positions: Record<string, MermaidSvgNodePosition> = {}

    for (const nodeElement of nodeElements) {
      const nodeId = getNodeIdFromMermaidNodeElement(nodeElement)
      if (!nodeId) continue

      const topLeft = getTopLeftInSvgCoordinates(nodeElement)
      if (!topLeft) continue

      positions[nodeId] = topLeft
    }

    return normalizePositions(positions, padding)
  } finally {
    container.remove()
  }
}
